import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import * as React from "react"

import { DataTable, type Column } from "@/components/data-table"
import { Badge, PageHeader } from "@/components/ui"
import { useExpiring, type Expiring } from "@/features/reports/api"
import {
  BookRenewalButton,
  BookRenewalDialog,
  useCanBookRenewal,
} from "@/features/reports/book-renewal"
import {
  ExpiryGroups,
  timeLeft,
  toneForDays,
} from "@/features/reports/expiry-groups"
import { PAGE_SIZE } from "@/lib/constants"
import { linkText } from "@/lib/link"
import { cn, formatDate } from "@/lib/utils"

export const Route = createFileRoute("/expiring")({ component: ExpiringScreen })

/** The course a booking is for, held while its confirmation is open. */
type Booking = { interventionId: number; intervention: string }

function ExpiringScreen() {
  const navigate = useNavigate()
  const [page, setPage] = React.useState(1)
  const [days, setDays] = React.useState(90)
  // The course being booked, held here rather than in the cell that offers
  // it: a dialog rendered inside a row hands its clicks to that row's own
  // click handler, because a React event bubbles up the component tree even
  // when the DOM node has been portalled out of the table.
  const [booking, setBooking] = React.useState<Booking | null>(null)

  const query = useExpiring(page, days)
  const canBookRenewal = useCanBookRenewal()

  const columns: Column<Expiring>[] = [
    {
      key: "employee_name",
      header: "Learner",
      primary: true,
      cell: (row) => (
        <div>
          <span className="font-medium">{row.employee_name}</span>
          <span className="block text-xs text-muted-foreground">
            {row.employee_number}
          </span>
        </div>
      ),
    },
    {
      key: "intervention",
      header: "Training",
      cell: (row) => row.intervention,
    },
    {
      key: "expiry_date",
      header: "Expires",
      cell: (row) => formatDate(row.expiry_date),
    },
    {
      key: "days_left",
      header: "Time left",
      cell: (row) => (
        // Banded rather than a bare number, because "in 12 days" and "in 140
        // days" call for different actions and a column of integers makes the
        // reader do that sorting in their head.
        //
        // The edges come from `expiry-groups`, which is also what the summary
        // above sends the server. This column used to band at 30 and 90 while
        // the server's own bands close at 30 and 60, so a certificate 75 days
        // out would have been drawn as a warning here and as routine in the
        // summary directly above it. One definition, one screen.
        <Badge tone={toneForDays(row.days_left)}>{timeLeft(row.days_left)}</Badge>
      ),
    },
    {
      key: "record_number",
      header: "Record",
      // Kept off the mobile card deliberately, along with the action below
      // it. The card is itself one big button, and an anchor or a button
      // nested inside a button is invalid HTML; tapping the card already
      // opens the record, which is what this column offers.
      hideOnMobile: true,
      cell: (row) => (
        <Link
          to="/records/$recordId"
          params={{ recordId: String(row.id) }}
          // The row click goes to the same place, so letting this bubble
          // would navigate twice for one click.
          onClick={(event) => event.stopPropagation()}
          className={cn("font-mono text-xs text-muted-foreground", linkText)}
        >
          {row.record_number}
        </Link>
      ),
    },
    {
      key: "renewal",
      header: "Renewal",
      hideOnMobile: true,
      cell: (row) =>
        canBookRenewal(row.intervention_id) ? (
          <BookRenewalButton
            onOpen={() =>
              setBooking({
                interventionId: row.intervention_id as number,
                intervention: row.intervention,
              })
            }
          />
        ) : null,
    },
  ]

  return (
    <>
      <PageHeader
        eyebrow="Insights"
        title="Expiring training"
        description="Signed-off training that is still in date but will lapse. Soonest first, so the next course to book is the top row."
      />

      {/* Triage above the detail (worklist item 14). Forty rows of people is
          the evidence; four courses is the decision, and the decision is what
          somebody opens this screen to make. The horizon lives up here with
          it because it governs both: two controls for one window is two
          chances to set it differently, which is the same reasoning the
          reports screen's single reporting period carries. */}
      <ExpiryGroups
        days={days}
        onDaysChange={(next) => {
          setDays(next)
          setPage(1)
        }}
        onBook={(group) =>
          setBooking({
            interventionId: group.intervention_id,
            intervention: group.intervention,
          })
        }
      />

      <DataTable
        columns={columns}
        rows={query.data?.items ?? []}
        count={query.data?.count ?? 0}
        rowKey={(row) => row.id}
        page={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        isLoading={query.isLoading}
        isRefreshing={query.isFetching && !query.isPending}
        isError={query.isError}
        onRetry={() => void query.refetch()}
        onRowClick={(row) =>
          void navigate({
            to: "/records/$recordId",
            params: { recordId: String(row.id) },
          })
        }
        emptyTitle="Nothing lapses in that window"
        emptyDescription="Only signed-off records with an expiry date appear here. Training that never expires is not listed, and training that has already lapsed is on the records screen."
      />

      {booking && (
        <BookRenewalDialog
          interventionId={booking.interventionId}
          intervention={booking.intervention}
          days={days}
          onClose={() => setBooking(null)}
        />
      )}
    </>
  )
}
